import { api } from "./api";
import type { Paginated, PageSize } from "./pagination";

export interface UserSummary {
  user_id: string;
  username: string;
  email: string;
  full_name: string | null;
  organization: string | null;
  state: "active" | "disabled";
  groups: string[];
  /** Whether this operator has 2FA enrolled (ADR-0017). */
  totp_enabled?: boolean;
  /** Set ⇒ this membership belongs to a central Account Tray identity
   *  (ADR-0077): no local password, logs in via `app.<base>`. */
  identity_id?: string | null;
  last_login_at: string | null;
  created_at: string;
}

/** Operator security flags — governed by the `users` RBAC, lives in the Tray
 *  users admin (the app_user 2FA flag stays on the portal Security page).
 *  `local_accounts_enabled` is the ADR-0077 per-workspace local-login door. */
export interface OperatorSecurity {
  totp_required_users: boolean;
  local_accounts_enabled: boolean;
}

/** Invite an Account Tray person by email (ADR-0077 S3). */
export interface InviteUserBody {
  email: string;
  full_name?: string | null;
  groups?: string[];
}

export interface InviteUserOut {
  user: UserSummary;
  /** `invited` (fresh identity → claim link) | `linked` (already claimed). */
  mode: string;
  /** The copyable set-a-password link — the guarantee; the mail is the convenience. */
  claim_url: string | null;
  login_url: string | null;
  mail_sent: boolean;
}

export interface UserCreate {
  username: string;
  email: string;
  password: string;
  full_name?: string;
  organization?: string;
  groups?: string[];
}

export interface UserUpdate {
  email?: string;
  full_name?: string | null;
  organization?: string | null;
  state?: "active" | "disabled";
  groups?: string[];
}

/** F6b: structured entry returned by /groups/known-permissions for
 *  the matrix picker. One per resource; ``actions`` lists the verbs
 *  that apply to it. Permissions are plain ``resource:action`` — tags
 *  are organizational labels, not a security concept. */
export interface ResourceCatalogEntry {
  resource: string;
  actions: string[];
  /** Product plane (sidebar rail section): data | flows | apps | odino | admin. */
  group: string;
}

export interface GroupSummary {
  group_id: string;
  name: string;
  description: string | null;
  // Canonical plain "resource:action" strings. The backend's
  // GroupSummary validator normalises any legacy row (tuple or
  // tag-scoped) to this format on the wire, so the UI can compare
  // them with string equality.
  permissions: string[];
  created_at: string;
}

export interface ApiKeySummary {
  key_id: string;
  name: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface GroupCreate {
  group_id: string;
  name: string;
  description?: string | null;
  permissions?: string[];
}

export interface GroupUpdate {
  name?: string;
  description?: string | null;
  permissions?: string[];
}

export interface GroupDetail extends GroupSummary {
  members: UserSummary[];
}

export const usersApi = {
  list: (params?: { page?: number; page_size?: PageSize }) =>
    api.get<Paginated<UserSummary>>("/users", { query: params }),
  get: (id: string) => api.get<UserSummary>(`/users/${id}`),
  create: (body: UserCreate) => api.post<UserSummary>("/users", body),
  /** Invite an Account Tray person by EMAIL (ADR-0077 S3): Control creates or
   *  finds the central identity + mails the claim link; the membership row is
   *  created password-less. Only available on a subdomain-resolved SaaS tenant. */
  invite: (body: InviteUserBody) => api.post<InviteUserOut>("/users/invite", body),
  update: (id: string, body: UserUpdate) => api.patch<UserSummary>(`/users/${id}`, body),
  /** Operator reset of another operator's 2FA (ADR-0017) — the lost-device
   *  escape hatch. Clears the factor; the user re-enrolls on next login. */
  resetTwoFactor: (id: string) => api.post<UserSummary>(`/users/${id}/reset-2fa`),
  /** Operator reset of another operator's PASSWORD — the "forgot my
   *  password" escape hatch (no old-password proof; self-service change
   *  lives at /auth/change-password). */
  setPassword: (id: string, password: string) =>
    api.post<UserSummary>(`/users/${id}/set-password`, { password }),
  /** Kick the user out NOW: bumps token_version (outstanding access
   *  JWTs die at their next request) + retires all refresh tokens. */
  revokeSessions: (id: string) =>
    api.post<UserSummary>(`/users/${id}/revoke-sessions`),
  /** The operator 2FA-required policy (ADR-0017). */
  getSecurity: () => api.get<OperatorSecurity>("/users/security"),
  updateSecurity: (body: OperatorSecurity) =>
    api.put<OperatorSecurity>("/users/security", body),

  listGroups: (params?: { page?: number; page_size?: PageSize }) =>
    api.get<Paginated<GroupSummary>>("/users/groups", { query: params }),
  getGroup: (groupId: string) =>
    api.get<GroupDetail>(`/users/groups/${groupId}`),
  createGroup: (body: GroupCreate) =>
    api.post<GroupSummary>("/users/groups", body),
  updateGroup: (groupId: string, body: GroupUpdate) =>
    api.patch<GroupSummary>(`/users/groups/${groupId}`, body),
  deleteGroup: (groupId: string) =>
    api.delete<void>(`/users/groups/${groupId}`),
  addGroupMember: (groupId: string, userId: string) =>
    api.post<GroupDetail>(`/users/groups/${groupId}/members`, {
      user_id: userId,
    }),
  removeGroupMember: (groupId: string, userId: string) =>
    api.delete<GroupDetail>(`/users/groups/${groupId}/members/${userId}`),
  knownPermissions: () =>
    api.get<{
      permissions: string[];
      resources: ResourceCatalogEntry[];
    }>("/users/groups/known-permissions"),

  // Admin oversight of a user's keys — LIST + REVOKE only (ADR-0052): an
  // operator can no longer mint a key on behalf of another user (that was a
  // persistent impersonation credential). Personal tokens are self-serve via
  // `/me/api-keys`; machine integrations are Service Accounts.
  listApiKeys: (userId: string) => api.get<ApiKeySummary[]>(`/users/${userId}/api-keys`),
  revokeApiKey: (userId: string, keyId: string) =>
    api.delete<void>(`/users/${userId}/api-keys/${keyId}`),
};
